import {
  Autocomplete,
  Card,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TextField,
  Tooltip,
} from '@mui/material';
import { debounce } from 'lodash';
import Page from 'src/components/Page';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useTable from 'src/hooks/useTable';
import { FilterList as FilterListIcon, GroupAdd } from '@mui/icons-material';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import { PATH_DASHBOARD } from 'src/routes/paths';
import FilterDrawer from 'src/components/FilterDrawer';
import { PaginateQueryWithStats } from 'src/api/pagination/dtos';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import LoadingTableRow from 'src/components/table/LoadingTableRow';
import { TableHeadCustom, TableNoData } from 'src/components/table';
import PatientEngagementRow from './components/PatientEngagementRow';
import { TABLE_HEAD_PATIENT_ENGAGEMENT_LIST } from './components/table-head';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { translateTableHeadCustom } from 'src/careflow/helpers/common';
import { PatientEngagementService } from 'src/careflow/engagement-hub/patient-engagement/service/PatientService';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import EngagementListToolbar from './components/EngagementListToolbar';
import EngagementFiltersList, {
  EngagementFiltersListValues,
} from './components/EngagementFiltersList';
import {
  isReservedSearchTerm,
  ListSearchFilterOption,
} from 'src/sections/engagement/config/engagementListSearchConfig';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import StandaloneCustomPagination from 'src/careflow/common/StandaloneCustomPagination';
import { defaultRowsPerPageOptions } from 'src/careflow/helpers/constants';

interface PatientEngagementListProps {
  includeBreadcrumbs?: boolean;
  rows: any;
  setRows: Dispatch<any>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  totalItems: number;
  setTotalItems: Dispatch<SetStateAction<number>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  assignedTo: any;
  setAssignedTo: Dispatch<any>;
  workflowStage: string;
  setWorkFlowStage: Dispatch<SetStateAction<string>>;
  includeStats?: boolean;
  setStats?: Dispatch<SetStateAction<any>>;
  show?: boolean;
  showFilters?: boolean;
}

export default function PatientEngagementList({
  includeBreadcrumbs,
  isLoading,
  setIsLoading,
  search,
  setSearch,
  rows,
  setRows,
  totalItems,
  setTotalItems,
  show = true,
  assignedTo,
  setAssignedTo,
  includeStats = false,
  setStats,
  workflowStage,
  setWorkFlowStage,
  showFilters = true,
}: PatientEngagementListProps) {
  const {
    page,
    rowsPerPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onChangePage,
    onChangeRowsPerPage,
    setPage,
  } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const { themeStretch } = useSettings();
  const { i18n } = useLocales();
  const user = useAuth().getCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const [allUsers, setAllUsers] = useState([]);
  const [openAssignTo, setOpenAssignTo] = useState(false);
  const [valueAuto, setValueAuto] = useState<any>(null);
  const [workflowStatus, setWorkflowStatus] = useState<string>('');
  const [workflowScope, setWorkflowScope] = useState<string>('');
  const [scopeOptions, setScopeOptions] = useState<ListSearchFilterOption[]>([]);
  const [stageOptionsFromStats, setStageOptionsFromStats] = useState<ListSearchFilterOption[]>([]);
  const [isOpenFilterDrawer, setIsOpenFilterDrawer] = useState<boolean>(false);
  const [draftFilters, setDraftFilters] = useState<EngagementFiltersListValues>({
    workflowStatus: '',
    workflowScope: '',
    workflowStageName: '',
    assignedToMe: false,
  });

  /** Search term only applied to API when not a reserved (non-searchable) term */
  const effectiveSearch = isReservedSearchTerm(search) ? '' : search;

  const getPaginateQuery = (
    page: number,
    rowsPerPage: number,
    searchParam: string,
    assignedTo: any,
    stage: string,
    status: string,
    scope: string
  ) => {
    const paginateQuery: PaginateQueryWithStats = { page: page, limit: rowsPerPage };
    if (searchParam) {
      paginateQuery.search = searchParam;
    }
    paginateQuery.filter = {};

    if (assignedTo) {
      paginateQuery.filter.workflowOwnedBy =
        typeof assignedTo === 'boolean' ? `$eq:${user?.userName}` : `$eq:${assignedTo}`;
    }

    if (stage) {
      paginateQuery.filter.workflowStageName = `$eq:${stage}`;
    }

    if (status) {
      paginateQuery.filter.workflowStatus = `$eq:${status}`;
    }

    if (scope) {
      paginateQuery.filter.workflowScope = `$eq:${scope}`;
    }

    if (includeStats) {
      paginateQuery.includeStats = true;
    }

    return paginateQuery;
  };

  const getPatientEngagements = async (
    page: number,
    rowsPerPage: number,
    searchParam: string,
    assignedTo: any,
    stage: string,
    status: string,
    scope: string
  ) => {
    try {
      setIsLoading(true);
      const patientEngagements = await PatientEngagementService.getAll(
        getPaginateQuery(page, rowsPerPage, searchParam, assignedTo, stage, status, scope)
      );
      setRows(patientEngagements);
      if (includeStats && setStats && patientEngagements?.stats) {
        setStats(patientEngagements?.stats);
      }
      if (patientEngagements?.stats && Array.isArray(patientEngagements.stats)) {
        setStageOptionsFromStats(
          patientEngagements.stats.map((s: { code?: string; label?: string }) => ({
            value: s?.code ?? '',
            label: s?.label ?? s?.code ?? '',
          }))
        );
      }
      if (patientEngagements?.meta?.totalItems !== undefined)
        setTotalItems(patientEngagements?.meta?.totalItems);
      setIsLoading(false);
    } catch (e) {
      console.log(`Something failed while trying to load patient engagements ${e}`);
    }
  };

  const getPatientEngagementsList = useCallback(debounce(getPatientEngagements, 600), []);

  const fetchAllUsers = async () => {
    try {
      const getAllUsers = await UserService.getAllUsers();
      const uniqueUsers: any = [];
      const userNamesSet = new Set();

      getAllUsers?.forEach((user: any) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        if (!userNamesSet.has(fullName)) {
          uniqueUsers.push(user);
          userNamesSet.add(fullName);
        }
      });
      uniqueUsers.sort((a: any, b: any) => {
        const fullNameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const fullNameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return fullNameA.localeCompare(fullNameB);
      });

      setAllUsers(uniqueUsers);
    } catch (err) {
      console.log('error on fetching all users', err);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const fetchScopeOptions = async () => {
    try {
      const workflows = await WorkflowService.getWorkflowByType('ehub-box');
      const active = Array.isArray(workflows)
        ? workflows.filter((w: any) => w.status === 'Active')
        : [];
      setScopeOptions(
        active.map((w: { code?: string; name?: string }) => ({
          value: w?.code ?? '',
          label: w?.name ?? w?.code ?? '',
        }))
      );
    } catch (err) {
      console.log('error fetching engagement scope options', err);
    }
  };

  useEffect(() => {
    if (showFilters) fetchScopeOptions();
  }, [showFilters]);

  useEffect(() => {
    getPatientEngagementsList(
      page,
      rowsPerPage,
      effectiveSearch,
      assignedTo,
      workflowStage,
      workflowStatus,
      workflowScope
    );
  }, [
    page,
    rowsPerPage,
    effectiveSearch,
    assignedTo,
    workflowStage,
    workflowStatus,
    workflowScope,
  ]);

  const handleRowsPerPageChange = (newSize: number) => {
    onChangeRowsPerPage({ target: { value: String(newSize) } } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleSearch = (query: string) => {
    setSearch(query);
    setPage(1);
  };

  const handlerBulkAssignTo = async (userToAssign: any) => {
    try {
      if (!userToAssign) return;
      await PatientEngagementService.bulkAssignOwner({
        selectedPatients: selected,
        userToAssign,
        referenceName: 'ENGAGEMENT',
      });
      enqueueSnackbar('User was assigned');
    } catch (err) {
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    } finally {
      setOpenAssignTo(false);
      setValueAuto(null);
      setSelected([]);
      getPatientEngagementsList(
        page,
        rowsPerPage,
        effectiveSearch,
        assignedTo,
        workflowStage,
        workflowStatus,
        workflowScope
      );
    }
  };

  const handleOpenFilterDrawer = () => {
    setDraftFilters({
      workflowStatus,
      workflowScope,
      workflowStageName: workflowStage,
      assignedToMe: !!assignedTo,
    });
    setIsOpenFilterDrawer(true);
  };

  const handleApplyFilters = () => {
    setWorkflowStatus(draftFilters.workflowStatus);
    setWorkflowScope(draftFilters.workflowScope);
    setWorkFlowStage(draftFilters.workflowStageName);
    setAssignedTo(draftFilters.assignedToMe ? true : null);
    setPage(1);
    setIsOpenFilterDrawer(false);
  };

  const handleClearAllFilters = () => {
    setDraftFilters({
      workflowStatus: '',
      workflowScope: '',
      workflowStageName: '',
      assignedToMe: false,
    });
    setWorkflowStatus('');
    setWorkflowScope('');
    setWorkFlowStage('');
    setAssignedTo(null);
    setPage(1);
    setIsOpenFilterDrawer(false);
  };

  if (!show) {
    return null;
  }

  return (
    <Page title={i18n('patientEngagement.title', 'engagement')}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        {includeBreadcrumbs ? (
          <HeaderBreadcrumbs
            title={i18n('patientEngagement.title', 'engagement')}
            heading=""
            links={[
              { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
              { name: `${i18n('title', 'engagement')}` },
              {
                name: `${i18n('patientEngagement.title', 'engagement')}`,
                href: PATH_DASHBOARD.engagementHub.patientEngagement,
              },
            ]}
          />
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Stack
                direction="row"
                sx={{ paddingTop: 2, px: 2 }}
                alignItems="center"
                flexWrap="wrap"
              >
                <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                  <EngagementListToolbar
                    search={search}
                    onSearch={(value) => {
                      handleSearch(value);
                      setPage(1);
                    }}
                  />
                </Grid>
                <Grid
                  container
                  xs={12}
                  md={6}
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                >
                  {showFilters ? (
                    <Tooltip title="Filter">
                      <IconButton
                        onClick={handleOpenFilterDrawer}
                        sx={{
                          height: '40px',
                          backgroundColor:
                            workflowStatus || workflowScope || workflowStage || assignedTo,
                        }}
                      >
                        <FilterListIcon
                          htmlColor={workflowStatus || workflowScope || workflowStage || assignedTo}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : null}

                  <Tooltip title="Assign To">
                    <IconButton
                      sx={{ my: 2 }}
                      onClick={() => setOpenAssignTo(true)}
                      disabled={selected.length > 0 ? false : true}
                    >
                      <GroupAdd sx={{ color: selected.length > 0 ? '#00AB55' : '' }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHeadCustom
                    headLabel={translateTableHeadCustom(
                      i18n,
                      TABLE_HEAD_PATIENT_ENGAGEMENT_LIST,
                      'engagement'
                    )}
                    rowCount={rows?.data?.length}
                    numSelected={selected.length}
                    isLoading={isLoading}
                    onSelectAllRows={(checked) => {
                      onSelectAllRows(
                        checked,
                        rows?.data?.map((row: any) => row.patientEngagementId)
                      );
                    }}
                  />
                  <TableBody>
                    {!isLoading ? (
                      rows?.data?.length ? (
                        rows?.data?.map((row: any) => (
                          <PatientEngagementRow
                            key={row.patientEngagementId}
                            row={row}
                            selected={selected.includes(row.patientEngagementId)}
                            onSelectRow={() => onSelectRow(row.patientEngagementId)}
                          />
                        ))
                      ) : (
                        <TableNoData isNotFound={true} />
                      )
                    ) : (
                      <LoadingTableRow colSpan={TABLE_HEAD_PATIENT_ENGAGEMENT_LIST.length} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid xs={12}>
                <Stack justifyContent="flex-end">
                  <StandaloneCustomPagination
                    page={page}
                    totalItems={totalItems ?? 0}
                    pageSize={rowsPerPage}
                    rowsPerPageOptions={defaultRowsPerPageOptions}
                    onPageChange={(newPage) => onChangePage(null, newPage)}
                    onPageSizeChange={handleRowsPerPageChange}
                  />
                </Stack>
              </Grid>
            </Card>
          </Grid>
          <FilterDrawer
            title="Assign To"
            open={openAssignTo}
            anchor={'right'}
            onApplyButtonClick={() => {
              handlerBulkAssignTo(valueAuto?.id);
            }}
            onCloseIconButtonClick={() => {
              setOpenAssignTo(false);
            }}
            onClearAllButtonClick={() => {
              setValueAuto(null);
            }}
          >
            <br />
            <Autocomplete
              id="combo-box-assign-to"
              value={valueAuto}
              onChange={(_, user) => {
                setValueAuto(user);
              }}
              getOptionLabel={(user: any) => user?.label ?? ''}
              options={allUsers.map((user: any) => ({
                label: `${user?.firstName} ${user?.lastName}`,
                id: user?.id,
              }))}
              renderInput={(params) => <TextField label="Users" {...params} />}
            />
          </FilterDrawer>

          {showFilters ? (
            <FilterDrawer
              title={i18n('patientEngagement.filters.title', 'engagement')}
              anchor="right"
              open={isOpenFilterDrawer}
              onCloseIconButtonClick={() => setIsOpenFilterDrawer(false)}
              onApplyButtonClick={handleApplyFilters}
              onClearAllButtonClick={handleClearAllFilters}
            >
              <EngagementFiltersList
                values={draftFilters}
                onChange={(key, value) => setDraftFilters((prev) => ({ ...prev, [key]: value }))}
                scopeOptions={scopeOptions}
                stageOptions={stageOptionsFromStats}
                i18n={i18n}
              />
            </FilterDrawer>
          ) : null}
        </Grid>
      </Container>
    </Page>
  );
}
